import { decodeRedirectUrl } from "~/utils/sanitize-redirect-url";
import { useConfig } from "~/hooks/webconfig";
import { meetupProviderReturnUrl } from "~/utils/meetup-provider-return";
import { msg } from "@lingui/core/macro";
import { ActionFunctionArgs, data, HeadersFunction, LoaderFunctionArgs, MetaFunction, redirect } from "react-router";
import { Link, useLoaderData, useLocation, useSearchParams } from "react-router";
import { appleAuthorizeUrl, encodeOAuthState } from "~/utils/oauth-state";
import { issueOAuthNonce } from "~/utils/oauth-state.server";
import React, { FormEvent } from "react";
import { Login } from "~/components/authentication/";
import { i18n } from "~/i18n/i18n";
import { isSafari } from "~/utils/extension-version";
import metrics from "~/utils/metrics";
import { openAuthWindow } from "~/utils/open-auth-window";
import { graphql } from "~/graphql";
import { useMutation } from "@apollo/client/react";
import usePageView from "~/hooks/page-view";
import { AppRoutes } from "~/utils/app-routes";
import { getServerClient } from "~/graphql";
import {
  accessTokenCookie,
  loggedInCookie,
  refreshTokenCookie,
} from "~/utils/oauth.server";
import { csrfProtection } from "~/utils/ensure-authenticated.server";
import { sanitizeRedirectURl } from "~/utils/sanitize-redirect-url";
import { onboardingDestination, readMeetupIntent } from "~/utils/meetup-intent.server";
import { meetupReturnUrl } from "../../../shared/meetup-auth-intent.server";

const LoginDocument = graphql(`
  mutation Login(
    $email: String!
    $password: String!
    $state: String
    $inviteCode: String
  ) {
    login(
      body: {
        password: $password
        email: $email
        state: $state
        inviteCode: $inviteCode
      }
    ) {
      refreshToken
      accessToken
    }
  }
`);

const ForgotPasswordWithPinDocument = graphql(`
  mutation forgotPasswordWithPin($email: String!) {
    forgotPasswordWithPin(email: $email)
  }
`);

const ResetPasswordWithPinDocument = graphql(`
  mutation resetPasswordWithPin(
    $email: String!
    $pin: String!
    $password: String!
  ) {
    resetPasswordWithPin(email: $email, pin: $pin, password: $password)
  }
`);

const ValidateForgotPasswordPinDocument = graphql(`
  mutation validateForgotPasswordPin($email: String!, $pin: String!) {
    validateForgotPasswordPin(email: $email, pin: $pin)
  }
`);

const RequestLoginPinDocument = graphql(`
  mutation requestLoginPin($email: String!) {
    requestLoginPin(email: $email)
  }
`);

const LoginWithPinDocument = graphql(`
  mutation loginWithPin($email: String!, $pin: String!) {
    loginWithPin(email: $email, pin: $pin) {
      refreshToken
      accessToken
    }
  }
`);

// A VERIFIED meetup sign-in goes through onboarding: the arrival details are
// collected there, and onboarding returns to the exact event once they are
// saved. The decision is made on the signed intent, never on the destination's
// shape — a shape test matches every community live link, so cohort sessions and
// non-pilot meetups would pick up an extra onboarding screen they never had.
// Only a token minted by web.space's signup route (which is pilot-gated) may
// divert a login, which keeps the pilot's blast radius exactly as scoped.
// An expired token is not a silent failure: the attendee lands on the event and
// the ArrivalCard's missing-details form collects the fields and checks them in.
// That fallback is deliberate and documented, and strict pilot scoping is worth
// more than avoiding one extra screen for an attendee whose token expired.
// Ordinary logins are untouched.
function loginDestination(request: Request, state: string) {
  const supplied = new URL(request.url).searchParams.get("meetupIntent");
  if (supplied) {
    const returnUrl = meetupReturnUrl(sanitizeRedirectURl(state), request.url);
    const verified = returnUrl
      ? readMeetupIntent(request, returnUrl, supplied)
      : null;
    if (verified) return onboardingDestination(verified.returnUrl, supplied);
  }
  return sanitizeRedirectURl(state);
}

// Binds every provider sign-in started on this page to this browser
// (FOX-4690): the nonce goes into the OAuth state and an HttpOnly cookie, and
// the callback refuses unless they match.
export function loader({ request }: LoaderFunctionArgs) {
  const { nonce, setCookie } = issueOAuthNonce(request);
  return data(
    { oauthNonce: nonce },
    { headers: { "Set-Cookie": setCookie, "Cache-Control": "no-store" } },
  );
}

// The document response carries the loader's no-store (a route without a
// headers export passes on only Set-Cookie): the page embeds this browser's
// nonce, so no cache should hand it to anyone else.
export const headers: HeadersFunction = ({ parentHeaders, loaderHeaders }) => {
  const merged = new Headers(parentHeaders);
  merged.set("Cache-Control", loaderHeaders.get("Cache-Control") ?? "no-store");
  return merged;
};

export async function action({ request }: ActionFunctionArgs) {
  csrfProtection(request);
  const url = new URL(request.url);
  try {
    const formData = await request.formData();

    let email = formData.get("email") as string;
    let password = formData.get("password") as string;
    let state = formData.get("state") as string;
    let inviteCode = formData.get("inviteCode") as string;
    let intent = (formData.get("intent") as string) || "";
    let pin = (formData.get("pin") as string) || "";

    if (intent === "request-login-pin") {
      const { error } = await getServerClient().mutate({
        mutation: RequestLoginPinDocument,
        variables: { email },
        fetchPolicy: "no-cache",
        context: {
          headers: {
            "x-forwarded-for":
              request.headers.get("x-forwarded-for") || undefined,
          },
        },
      });
      if (error) throw error;
      return data({ pinRequested: true, email }, 200);
    }

    if (intent === "login-with-pin") {
      const { data: loginWithPinData, error } = await getServerClient().mutate({
        mutation: LoginWithPinDocument,
        variables: { email, pin },
        fetchPolicy: "no-cache",
        context: {
          headers: {
            "x-forwarded-for":
              request.headers.get("x-forwarded-for") || undefined,
          },
        },
      });
      if (error) throw error;

      const headers = new Headers();
      headers.append("Set-Cookie", loggedInCookie());
      headers.append(
        "Set-Cookie",
        accessTokenCookie(loginWithPinData?.loginWithPin?.accessToken || ""),
      );
      headers.append(
        "Set-Cookie",
        refreshTokenCookie(loginWithPinData?.loginWithPin?.refreshToken || ""),
      );

      const redirectUrl = loginDestination(request, state);
      return redirect(redirectUrl ? redirectUrl : "/", { headers });
    }

    const { data: loginData, error } = await getServerClient().mutate({
      mutation: LoginDocument,
      variables: {
        email,
        password: password || "",
        state: state || "",
        inviteCode: inviteCode || undefined,
      },
      fetchPolicy: "no-cache",
      context: {
        headers: {
          "x-forwarded-for":
            request.headers.get("x-forwarded-for") || undefined,
        },
      },
    });
    if (error) {
      throw error;
    }

    const headers = new Headers();
    headers.append("Set-Cookie", loggedInCookie());
    headers.append(
      "Set-Cookie",
      accessTokenCookie(loginData?.login?.accessToken || ""),
    );
    headers.append(
      "Set-Cookie",
      refreshTokenCookie(loginData?.login?.refreshToken || ""),
    );

    if (!password) {
      // No password path now handled by explicit intents; default to showing PIN
      // request UI. The destination context must survive the bounce: dropping
      // redirectUrl/meetupIntent here loses the event an attendee scanned into,
      // and a passwordless sign-in at the venue would finish on the generic home
      // page instead of the live hub.
      const carried = new URLSearchParams();
      const redirectUrlParam = url.searchParams.get("redirectUrl");
      const meetupIntentParam = url.searchParams.get("meetupIntent");
      if (redirectUrlParam) carried.set("redirectUrl", redirectUrlParam);
      if (meetupIntentParam) carried.set("meetupIntent", meetupIntentParam);
      carried.set("pinRequested", "true");
      return redirect(`/login?${carried.toString()}`);
    }
    const redirectUrl = loginDestination(request, state);
    return redirect(redirectUrl ? redirectUrl : "/", {
      headers,
      // status: 302,
    });
  } catch (e) {
    return data(
      {
        error: true,
      },
      400,
    );
  }
}

export const meta: MetaFunction = () => {
  return [
    {
      title: i18n._(msg`Mindstone - Login`),
    },
    { name: "description", content: i18n._(msg`Login to Mindstone`) },
  ];
};

export default function Index() {
  usePageView("Login - Choice");
  // const linkedInFeature = useFeature("linkedin-oauth");

  const [searchParams] = useSearchParams();
  const config = useConfig();
  const previousUrl = meetupProviderReturnUrl(searchParams, config.identityUrl);
  const { oauthNonce } = useLoaderData<typeof loader>();
  const oauthState = (redirectUrl: string) =>
    encodeOAuthState({
      redirectUrl,
      inviteCode: searchParams.get("inviteCode") || "",
      nonce: oauthNonce,
    });

  const params = new URLSearchParams(useLocation().search);

  let reset = false;
  if (params.get("reset")) {
    reset = decodeURIComponent(params.get("reset") || "") === "true";
  }

  let error = false;
  if (params.get("error")) {
    error = true;
  }

  let success = false;
  if (params.get("pinRequested") || params.get("success")) {
    success = true;
  }

  const handleGoogle = () => {
    metrics.track("authentication_start", { provider: "google" });
    const clientID =
      // eslint-disable-next-line lingui/no-unlocalized-strings -- OAuth client_id, not translatable UI copy
      "398058461162-6hdd0ko3de1gmefnkpsgiidhkd364lnh.apps.googleusercontent.com";
    const { oauthGoogleRedirectUrl } = webConfig;
    const state = oauthState(previousUrl);
    openAuthWindow(
      `https://accounts.google.com/o/oauth2/v2/auth?scope=email%20profile&response_type=code&state=${encodeURIComponent(state)}&prompt=select_account&redirect_uri=${encodeURIComponent(
        oauthGoogleRedirectUrl,
      )}&client_id=${clientID}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Google",
      previousUrl,
      "login",
    );
  };

  const handleApple = () => {
    metrics.track("authentication_start", { provider: "apple" });
    const clientID = "com.mindstone.web";
    const { oauthAppleRedirectUrl } = webConfig;
    // Safari: a full-page flow, so the state carries the destination and the
    // callback redirects straight to it.
    if (isSafari()) {
      window.location.href = appleAuthorizeUrl({
        clientId: clientID,
        redirectUri: oauthAppleRedirectUrl,
        state: oauthState(previousUrl),
      });
      return;
    }
    // Popup: no destination in the state, so the callback lands on the
    // success page, which messages this window.
    openAuthWindow(
      appleAuthorizeUrl({
        clientId: clientID,
        redirectUri: oauthAppleRedirectUrl,
        state: oauthState(""),
      }),
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Apple",
      previousUrl,
      "login",
    );
  };

  const handleMicrosoft = () => {
    metrics.track("authentication_start", { provider: "microsoft" });
    const { oauthMicrosoftRedirectUrl, oauthMicrosoftClientId } = config;
    const state = oauthState(previousUrl);
    openAuthWindow(
      // eslint-disable-next-line lingui/no-unlocalized-strings -- OAuth authorize URL
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?client_id=${encodeURIComponent(
        oauthMicrosoftClientId,
      )}&response_type=code&response_mode=query&scope=openid%20profile%20email&prompt=select_account&state=${encodeURIComponent(
        state,
      )}&redirect_uri=${encodeURIComponent(oauthMicrosoftRedirectUrl)}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Microsoft",
      previousUrl,
      "login",
    );
  };
  // Microsoft sign-in is configured per environment (OAUTH_MICROSOFT_CLIENT_ID,
  // served through the config query); no client id ⇒ no button.
  const microsoftConfigured = (config.oauthMicrosoftClientId ?? "") !== "";

  const handleLinkedin = () => {
    metrics.track("authentication_start", { provider: "linkedin" });
    // eslint-disable-next-line lingui/no-unlocalized-strings -- OAuth client ID
    const clientID = "78y1d813p23i03";

    const { oauthLinkedinRedirectUrl } = webConfig;
    const state = oauthState(previousUrl);
    openAuthWindow(
      `https://www.linkedin.com/oauth/v2/authorization?scope=openid%20profile%20email&response_type=code&state=${encodeURIComponent(state)}&redirect_uri=${encodeURIComponent(
        oauthLinkedinRedirectUrl,
      )}&client_id=${clientID}`,
      // eslint-disable-next-line lingui/no-unlocalized-strings -- window target identifier
      "Linkedin",
      previousUrl,
      "login",
    );
  };

  const [forgotPassword] = useMutation(ForgotPasswordWithPinDocument, {
    context: { skipAuth: true },
  });
  const [resetPassword] = useMutation(ResetPasswordWithPinDocument, {
    context: { skipAuth: true },
  });
  const [validateForgotPasswordPin] = useMutation(
    ValidateForgotPasswordPinDocument,
    {
      context: { skipAuth: true },
    },
  );

  let redirectUrl = "";
  if (params.get("redirectUrl")) {
    redirectUrl = decodeRedirectUrl(params.get("redirectUrl") || "/");
  }

  const submitForgot = async (e: FormEvent, email: string) => {
    e.preventDefault();
    try {
      await forgotPassword({
        variables: {
          email,
        },
      });
      return Promise.resolve(true);
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const resendPIN = async (e: FormEvent, email: string) => {
    e.preventDefault();
    try {
      await forgotPassword({
        variables: {
          email,
        },
      });
      return Promise.resolve(true);
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const submitReset = async (
    e: FormEvent,
    email: string,
    pin: string,
    password: string,
  ) => {
    e.preventDefault();
    try {
      await resetPassword({
        variables: {
          email,
          pin,
          password,
        },
      });
      return Promise.resolve(true);
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  const validatePin = async (e: FormEvent, email: string, pin: string) => {
    e.preventDefault();
    try {
      await validateForgotPasswordPin({
        variables: {
          email,
          pin,
        },
      });
      return Promise.resolve(true);
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  return (
    <Login
      handleGoogle={handleGoogle}
      handleApple={handleApple}
      handleLinkedin={undefined}
      handleMicrosoft={microsoftConfigured ? handleMicrosoft : undefined}
      reset={reset}
      success={success}
      error={error}
      appRoutes={AppRoutes}
      routerUtils={{ useLocation, Link }}
      submitForgot={submitForgot}
      resendPIN={resendPIN}
      submitReset={submitReset}
      validatePin={validatePin}
    />
  );
}
